export const isAsciiSafeSymbol = (symbol: string): boolean => {
  const safePdfSymbols = [
    '$', 'A$', 'C$', 'NZ$', 'S$', 'HK$', 'NT$', 'MX$', 'CI$', 'BD$',
    'RD$', 'J$', 'TT$', 'Bds$', 'EC$', 'B$', 'G$', 'FJ$', 'SI$',
    'Fr', 'kr', 'Rp', 'RM', 'Rs', 'Ft', 'lei', 'L', 'K', 'T', 'P',
    'B/.', 'Q', 'R$', 'R', 'Br', 'Bs.', 'S/.', 'Vt', 'MT', 'Kz',
    'N$', 'CFA', 'FCFA', 'CFP', 'D', 'Le', 'UM', 'Db', 'Ar',
    'MAD', 'DZD', 'DT', 'LD', 'SDG', 'Nfk', 'Sh', 'RF', 'CF', 'FG',
    'NAf', 'Sr$', 'KM', 'din', 'SM', 'T$', 'Esc', 'KSh', 'USh', 'TSh',
    'ZK', 'MK', 'Afl', 'BWP', 'SSP', 'AED', 'SAR', 'QAR', 'KWD',
    'BHD', 'OMR', 'JOD', 'IQD', 'IRR', 'YER',
  ];
  return safePdfSymbols.includes(symbol);
};

export function getCurrencyFallbackName(currency: string): string {
  return (currency || 'USD').toUpperCase();
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  // ── North America ──────────────────────────────────────────────
  USD: '$',        // United States
  CAD: 'C$',       // Canada
  MXN: 'MX$',      // Mexico
  GTQ: 'Q',        // Guatemala
  BZD: 'BZ$',      // Belize
  HNL: 'L',        // Honduras
  NIO: 'C$',       // Nicaragua
  CRC: '₡',        // Costa Rica
  PAB: 'B/.',       // Panama
  CUP: '$MN',      // Cuba
  DOP: 'RD$',      // Dominican Republic
  HTG: 'G',        // Haiti
  JMD: 'J$',       // Jamaica
  TTD: 'TT$',      // Trinidad & Tobago
  BBD: 'Bds$',     // Barbados
  XCD: 'EC$',      // East Caribbean (Antigua, Dominica, Grenada, St Kitts, St Lucia, St Vincent, Montserrat, Anguilla)
  BSD: 'B$',       // Bahamas
  KYD: 'CI$',      // Cayman Islands
  BMD: 'BD$',      // Bermuda
  AWG: 'Afl',      // Aruba
  ANG: 'NAf',      // Netherlands Antilles / Curaçao & Sint Maarten

  // ── South America ─────────────────────────────────────────────
  ARS: '$',        // Argentina
  BRL: 'R$',       // Brazil
  CLP: '$',        // Chile
  COP: '$',        // Colombia
  PEN: 'S/.',       // Peru
  BOB: 'Bs.',       // Bolivia
  PYG: '₲',        // Paraguay
  UYU: '$U',       // Uruguay
  VES: 'Bs.S',     // Venezuela
  GYD: 'G$',       // Guyana
  SRD: 'Sr$',      // Suriname
  FKP: '£',        // Falkland Islands

  // ── Europe ────────────────────────────────────────────────────
  EUR: '€',        // Eurozone (Austria, Belgium, Cyprus, Estonia, Finland, France, Germany, Greece,
                   //   Ireland, Italy, Latvia, Lithuania, Luxembourg, Malta, Netherlands, Portugal,
                   //   Slovakia, Slovenia, Spain + Andorra, Kosovo, Monaco, Montenegro, San Marino, Vatican)
  GBP: '£',        // United Kingdom
  CHF: 'Fr',       // Switzerland & Liechtenstein
  NOK: 'kr',       // Norway
  SEK: 'kr',       // Sweden
  DKK: 'kr',       // Denmark
  ISK: 'kr',       // Iceland
  PLN: 'zł',       // Poland
  CZK: 'Kč',       // Czech Republic
  HUF: 'Ft',       // Hungary
  RON: 'lei',      // Romania
  BGN: 'лв',       // Bulgaria
  HRK: 'kn',       // Croatia (replaced by EUR Jan 2023; kept for legacy)
  RSD: 'din',      // Serbia
  BAM: 'KM',       // Bosnia & Herzegovina
  MKD: 'ден',      // North Macedonia
  ALL: 'L',        // Albania
  MDL: 'L',        // Moldova
  UAH: '₴',        // Ukraine
  BYN: 'Br',       // Belarus
  RUB: '₽',        // Russia
  GEL: '₾',        // Georgia
  AMD: '֏',        // Armenia
  AZN: '₼',        // Azerbaijan
  TRY: '₺',        // Turkey
  GIP: '£',        // Gibraltar
  SHP: '£',        // Saint Helena, Ascension, Tristan da Cunha

  // ── Middle East & Western Asia ────────────────────────────────
  AED: 'د.إ',      // UAE
  SAR: '﷼',        // Saudi Arabia
  QAR: '﷼',        // Qatar
  KWD: 'د.ك',      // Kuwait
  BHD: '.د.ب',     // Bahrain
  OMR: '﷼',        // Oman
  JOD: 'JD',       // Jordan
  LBP: 'ل.ل',      // Lebanon
  SYP: '£S',       // Syria
  IQD: 'ع.د',      // Iraq
  IRR: '﷼',        // Iran
  YER: '﷼',        // Yemen
  ILS: '₪',        // Israel

  // ── Central & South Asia ──────────────────────────────────────
  INR: '₹',        // India
  PKR: '₨',        // Pakistan
  BDT: '৳',        // Bangladesh
  LKR: 'Rs',       // Sri Lanka
  NPR: 'रू',       // Nepal
  BTN: 'Nu',       // Bhutan
  MVR: 'Rf',       // Maldives
  AFN: '؋',        // Afghanistan

  // ── Central Asia ──────────────────────────────────────────────
  KZT: '₸',        // Kazakhstan
  UZS: "soʻm",     // Uzbekistan
  TJS: 'SM',       // Tajikistan
  KGS: 'лв',       // Kyrgyzstan
  TMT: 'T',        // Turkmenistan

  // ── East Asia ─────────────────────────────────────────────────
  CNY: '¥',        // China
  JPY: '¥',        // Japan
  KRW: '₩',        // South Korea
  KPW: '₩',        // North Korea
  HKD: 'HK$',      // Hong Kong
  MOP: 'P',        // Macau
  TWD: 'NT$',      // Taiwan
  MNT: '₮',        // Mongolia

  // ── Southeast Asia ────────────────────────────────────────────
  SGD: 'S$',       // Singapore
  MYR: 'RM',       // Malaysia
  IDR: 'Rp',       // Indonesia
  PHP: '₱',        // Philippines
  THB: '฿',        // Thailand
  VND: '₫',        // Vietnam
  KHR: '៛',        // Cambodia
  LAK: '₭',        // Laos
  MMK: 'K',        // Myanmar
  BND: 'B$',       // Brunei

  // ── Oceania ───────────────────────────────────────────────────
  AUD: 'A$',       // Australia, Kiribati, Nauru, Tuvalu
  NZD: 'NZ$',      // New Zealand, Cook Islands, Niue, Pitcairn, Tokelau
  FJD: 'FJ$',      // Fiji
  PGK: 'K',        // Papua New Guinea
  SBD: 'SI$',      // Solomon Islands
  VUV: 'Vt',       // Vanuatu
  WST: 'T',        // Samoa
  TOP: 'T$',       // Tonga
  XPF: 'CFP',      // CFP Franc (French Polynesia, New Caledonia, Wallis & Futuna)

  // ── North & East Africa ───────────────────────────────────────
  EGP: 'E£',       // Egypt
  SDG: 'SDG',      // Sudan
  SSP: 'SSP',      // South Sudan
  ETB: 'Br',       // Ethiopia
  ERN: 'Nfk',      // Eritrea
  DJF: 'Fdj',      // Djibouti
  SOS: 'Sh',       // Somalia
  KES: 'KSh',      // Kenya
  UGX: 'USh',      // Uganda
  TZS: 'TSh',      // Tanzania
  RWF: 'RF',       // Rwanda
  BIF: 'Fr',       // Burundi
  MWK: 'MK',       // Malawi
  ZMW: 'ZK',       // Zambia
  ZWL: 'Z$',       // Zimbabwe
  MOZ: 'MT',       // Mozambique (see MZN)
  MZN: 'MT',       // Mozambique

  // ── West Africa ───────────────────────────────────────────────
  NGN: '₦',        // Nigeria
  GHS: '₵',        // Ghana
  XOF: 'CFA',      // West African CFA (Benin, Burkina Faso, Guinea-Bissau, Ivory Coast,
                   //   Mali, Niger, Senegal, Togo)
  XAF: 'FCFA',     // Central African CFA (Cameroon, CAR, Chad, Congo, Equatorial Guinea, Gabon)
  GMD: 'D',        // Gambia
  GNF: 'FG',       // Guinea
  SLL: 'Le',       // Sierra Leone
  SLE: 'Le',       // Sierra Leone (redenominated 2022)
  LRD: 'L$',       // Liberia
  CVE: 'Esc',      // Cape Verde
  STN: 'Db',       // São Tomé & Príncipe
  MRU: 'UM',       // Mauritania

  // ── Central Africa ────────────────────────────────────────────
  CDF: 'Fr',       // DR Congo
  AOA: 'Kz',       // Angola

  // ── Southern Africa ───────────────────────────────────────────
  ZAR: 'R',        // South Africa, Namibia (also NAD), Lesotho (also LSL), Eswatini (also SZL)
  NAD: 'N$',       // Namibia
  BWP: 'P',        // Botswana
  LSL: 'L',        // Lesotho
  SZL: 'L',        // Eswatini (Swaziland)

  // ── Indian Ocean ──────────────────────────────────────────────
  MUR: 'Rs',       // Mauritius
  SCR: 'Rs',       // Seychelles
  KMF: 'CF',       // Comoros
  MGA: 'Ar',       // Madagascar

  // ── North Africa / Maghreb ────────────────────────────────────
  MAD: 'MAD',      // Morocco
  DZD: 'DZD',      // Algeria
  TND: 'DT',       // Tunisia
  LYD: 'LD',       // Libya


  };

export default CURRENCY_SYMBOLS;

const CURRENCY_MAP: Record<string, string> = {
  USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', JPY: 'Japanese Yen',
  AUD: 'Australian Dollar', CAD: 'Canadian Dollar', CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan', SEK: 'Swedish Krona', NZD: 'New Zealand Dollar',
  MXN: 'Mexican Peso', SGD: 'Singapore Dollar', HKD: 'Hong Kong Dollar',
  NOK: 'Norwegian Krone', KRW: 'South Korean Won', TRY: 'Turkish Lira',
  RUB: 'Russian Ruble', INR: 'Indian Rupee', BRL: 'Brazilian Real',
  ZAR: 'South African Rand', AED: 'UAE Dirham', SAR: 'Saudi Riyal',
  QAR: 'Qatari Riyal', KWD: 'Kuwaiti Dinar', BHD: 'Bahraini Dinar',
  OMR: 'Omani Rial', JOD: 'Jordanian Dinar', LBP: 'Lebanese Pound',
  EGP: 'Egyptian Pound', PKR: 'Pakistani Rupee', BDT: 'Bangladeshi Taka',
  LKR: 'Sri Lankan Rupee', NPR: 'Nepalese Rupee', MMK: 'Myanmar Kyat',
  THB: 'Thai Baht', VND: 'Vietnamese Dong', IDR: 'Indonesian Rupiah',
  MYR: 'Malaysian Ringgit', PHP: 'Philippine Peso', TWD: 'Taiwan Dollar',
  ILS: 'Israeli Shekel', DKK: 'Danish Krone', PLN: 'Polish Zloty',
  CZK: 'Czech Koruna', HUF: 'Hungarian Forint', RON: 'Romanian Leu',
  BGN: 'Bulgarian Lev', HRK: 'Croatian Kuna', RSD: 'Serbian Dinar',
  UAH: 'Ukrainian Hryvnia', KZT: 'Kazakhstani Tenge', AZN: 'Azerbaijani Manat',
  GEL: 'Georgian Lari', AMD: 'Armenian Dram', UZS: 'Uzbekistani Som',
  TJS: 'Tajikistani Somoni', KGS: 'Kyrgyzstani Som', TMT: 'Turkmenistan Manat',
  AFN: 'Afghan Afghani', IRR: 'Iranian Rial', IQD: 'Iraqi Dinar',
  SYP: 'Syrian Pound', YER: 'Yemeni Rial', MAD: 'Moroccan Dirham',
  TND: 'Tunisian Dinar', DZD: 'Algerian Dinar', LYD: 'Libyan Dinar',
  SDG: 'Sudanese Pound', SSP: 'South Sudanese Pound', ETB: 'Ethiopian Birr', KES: 'Kenyan Shilling',
  TZS: 'Tanzanian Shilling', UGX: 'Ugandan Shilling', GHS: 'Ghanaian Cedi',
  NGN: 'Nigerian Naira', XOF: 'West African CFA Franc', XAF: 'Central African CFA Franc',
  ZMW: 'Zambian Kwacha', MWK: 'Malawian Kwacha', MZN: 'Mozambican Metical',
  AOA: 'Angolan Kwanza', BWP: 'Botswana Pula', NAD: 'Namibian Dollar',
  SZL: 'Swazi Lilangeni', LSL: 'Lesotho Loti', MUR: 'Mauritian Rupee',
  SCR: 'Seychellois Rupee', MGA: 'Malagasy Ariary', MDL: 'Moldovan Leu',
  ALL: 'Albanian Lek', MKD: 'Macedonian Denar', BAM: 'Bosnia-Herzegovina Mark',
  ISK: 'Icelandic Krona', HNL: 'Honduran Lempira', GTQ: 'Guatemalan Quetzal',
  NIO: 'Nicaraguan Cordoba', CRC: 'Costa Rican Colon', PAB: 'Panamanian Balboa',
  BZD: 'Belize Dollar',
  CUP: 'Cuban Peso', DOP: 'Dominican Peso', HTG: 'Haitian Gourde',
  JMD: 'Jamaican Dollar', TTD: 'Trinidad & Tobago Dollar', BBD: 'Barbadian Dollar',
  BSD: 'Bahamian Dollar', KYD: 'Cayman Islands Dollar', ARS: 'Argentine Peso',
  CLP: 'Chilean Peso', COP: 'Colombian Peso', PEN: 'Peruvian Sol',
  BOB: 'Bolivian Boliviano', PYG: 'Paraguayan Guarani', UYU: 'Uruguayan Peso',
  VES: 'Venezuelan Bolivar', GYD: 'Guyanese Dollar', SRD: 'Surinamese Dollar',
  FJD: 'Fijian Dollar', PGK: 'Papua New Guinea Kina', WST: 'Samoan Tala',
  TOP: 'Tongan Paanga', SBD: 'Solomon Islands Dollar', VUV: 'Vanuatu Vatu',
  KHR: 'Cambodian Riel', LAK: 'Lao Kip', MNT: 'Mongolian Tugrik',
  BND: 'Brunei Dollar', MVR: 'Maldivian Rufiyaa', BTN: 'Bhutanese Ngultrum',
  MOP: 'Macanese Pataca', KPW: 'North Korean Won',
  // Additional
  GNF: 'Guinean Franc', SLL: 'Sierra Leonean Leone', LRD: 'Liberian Dollar',
  GMD: 'Gambian Dalasi', CVE: 'Cape Verdean Escudo', STN: 'Sao Tome Dobra',
  KMF: 'Comorian Franc', DJF: 'Djiboutian Franc', ERN: 'Eritrean Nakfa',
  SOS: 'Somali Shilling', RWF: 'Rwandan Franc', BIF: 'Burundian Franc',
  CDF: 'Congolese Franc', ZWL: 'Zimbabwean Dollar', MRU: 'Mauritanian Ouguiya',
  GIP: 'Gibraltar Pound', FKP: 'Falkland Islands Pound', SHP: 'Saint Helena Pound',
  AWG: 'Aruban Florin', ANG: 'Netherlands Antillean Guilder', XCD: 'East Caribbean Dollar',
  BMD: 'Bermudian Dollar', XPF: 'CFP Franc', BYN: 'Belarusian Ruble',
  SLE: 'Sierra Leonean Leone (new)',
};

export { CURRENCY_MAP };

export function fetchAllCurrencies(): Promise<Record<string, string>> {
  return Promise.resolve(CURRENCY_MAP);
}

export function getCurrencySymbol(currency: string): string {
  if (!currency || typeof currency !== 'string') return '$';
  return CURRENCY_SYMBOLS[currency.toUpperCase()] ?? currency;
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${amount.toFixed(2)}`;
}

export async function fetchLiveRates(base = 'USD'): Promise<Record<string, number>> {
  return {};
}

export async function convertCurrency(amount: number, from: string, to: string): Promise<number> {
  return amount;
}

export function getCurrencyDisplay(currency: string, mode: 'symbol' | 'code' = 'symbol'): string {
  if (mode === 'code') return (currency || 'USD').toUpperCase();
  return getCurrencySymbol(currency);
}